import { Apostle } from '@apeiron/apostle'
import { queryGet } from '@apeiron/common'
import { PlanetLite } from '@apeiron/planet'
import { StarSuperLite } from '@apeiron/star'
import { MayNull, QUERY, useAccountContext } from '@apeiron/temp'
import { useWalletAddress } from '@apeiron/wallet'
import { OrbitalTrackLite } from '@src/types/orbitalTrack'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { useCallback } from 'react'

const useCheckOwner = (): Hook => {
  const { query } = useRouter()

  const { account } = useAccountContext()

  const { compareAddress } = useWalletAddress()

  const checkPathOwner = useCallback(() => {
    const targetAddress = queryGet<string>(query, QUERY.OWNER_ADDRESS)

    const accountAddress = R.prop('walletAddress', account)

    return compareAddress(targetAddress, accountAddress)
  }, [account, query])

  const checkPlanetOwner = useCallback(
    (planet: MayNull<PlanetLite>): boolean => {
      const targetAddress = R.pathOr('', ['owner', 'walletAddress'], planet)

      const accountAddress = R.prop('walletAddress', account)

      return compareAddress(targetAddress, accountAddress)
    },
    [account, compareAddress]
  )

  const checkApostleOwner = useCallback(
    (apostle: MayNull<Apostle>): boolean => {
      const targetAddress = R.pathOr('', ['owner', 'walletAddress'], apostle)

      const accountAddress = R.prop('walletAddress', account)

      return compareAddress(targetAddress, accountAddress)
    },
    [account, compareAddress]
  )

  const checkStarOwner = useCallback(
    (star: MayNull<StarSuperLite>): boolean => {
      const targetAddress = R.pathOr('', ['owner', 'walletAddress'], star)

      const accountAddress = R.prop('walletAddress', account)

      return compareAddress(targetAddress, accountAddress)
    },
    [account, compareAddress]
  )

  const checkOrbitalTrackOwner = useCallback(
    (track: MayNull<OrbitalTrackLite>): boolean => {
      const targetAddress = R.pathOr(
        '',
        ['star', 'owner', 'walletAddress'],
        track
      )

      const accountAddress = R.prop('walletAddress', account)

      return compareAddress(targetAddress, accountAddress)
    },
    [account, compareAddress]
  )

  return {
    checkPathOwner,
    checkPlanetOwner,
    checkApostleOwner,
    checkStarOwner,
    checkOrbitalTrackOwner
  }
}

type Hook = {
  checkPathOwner: () => boolean
  checkPlanetOwner: (planet: MayNull<PlanetLite>) => boolean
  checkApostleOwner: (apostle: MayNull<Apostle>) => boolean
  checkStarOwner: (star: MayNull<StarSuperLite>) => boolean
  checkOrbitalTrackOwner: (track: MayNull<OrbitalTrackLite>) => boolean
}

export default useCheckOwner
